import { action, computed, observable } from 'mobx';
import { AssignmentPaymentType } from '~client/model/assignment-payment-type';
import { BaseStore } from '~web/store/base/base-store';
import { TokenAcceptDeclinePageParameters } from '~web/view/pages/parameters';

export enum AcceptDeclineState {
  NONE,
  CONFIRM,
  DECLINE,
}

export class AcceptDeclineFlowStore extends BaseStore {
  assignmentString: string;
  assigneeHasAccount: boolean;
  emailAddress: string;
  eventId: string;
  inviterFirstName: string;
  inviterLastName: string;
  paymentMethodType: AssignmentPaymentType;
  @observable activeState: AcceptDeclineState;
  @observable acceptOnWeb: boolean;

  /**
   * A flag that is set true if account email is same as invite email
   * only set if accept flow was started from logged in state
   */
  @observable accountEmailSameAsInvite: boolean;

  constructor() {
    super();
    this.activeState = AcceptDeclineState.NONE;
    this.acceptOnWeb = false;
    this.accountEmailSameAsInvite = false;
  }

  /**
   * Start the flow to accept user assignment tokens.
   * @param parameters
   */
  @action
  startConfirm(parameters: TokenAcceptDeclinePageParameters): void {
    this.setData(parameters);
    this.activeState = AcceptDeclineState.CONFIRM;
  }

  /**
   * Switch to the flow for accepting a user token assignment.
   * This will change the state, but keep the existing parameters.
   */
  @action
  switchToConfirm(): void {
    this.activeState = AcceptDeclineState.CONFIRM;
  }

  /**
   * Start the flow to decline user assignment tokens.
   * @param parameters
   */
  @action
  startDecline(parameters: TokenAcceptDeclinePageParameters): void {
    this.setData(parameters);
    this.activeState = AcceptDeclineState.DECLINE;
  }

  /**
   * Sets acceptOnWeb (whether to show accept on web overlay or not)
   * @param value
   */
  @action
  setAcceptOnWeb(value: boolean): void {
    this.acceptOnWeb = value;
  }

  /**
   * Sets accountEmailSameAsInvite (refer to the description of the variable)
   * @param value
   */
  @action
  setAccountEmailSameAsInvite(value: boolean): void {
    this.acceptOnWeb = value;
  }

  @computed
  get acceptDeclineFlowActive(): boolean {
    return this.activeState !== AcceptDeclineState.NONE;
  }

  /**
   * Finish any active flow
   */
  finish(): void {
    this.activeState = AcceptDeclineState.NONE;
    this.acceptOnWeb = false;
    this.accountEmailSameAsInvite = false;
    this.assignmentString = undefined;
    this.assigneeHasAccount = undefined;
    this.eventId = undefined;
    this.emailAddress = undefined;
    this.inviterFirstName = undefined;
    this.inviterLastName = undefined;
  }

  /**
   * Set all the necessary data
   * @param parameters
   */
  private setData(parameters: TokenAcceptDeclinePageParameters): void {
    const {
      assignmentString,
      assigneeHasAccount,
      eventId,
      emailAddress,
      inviterFirstName,
      inviterLastName,
      paymentMethodType,
    } = parameters;
    this.assignmentString = assignmentString;
    this.assigneeHasAccount = assigneeHasAccount === 'true';
    this.eventId = eventId;
    this.emailAddress = emailAddress;
    this.inviterFirstName = inviterFirstName;
    this.inviterLastName = inviterLastName;
    this.paymentMethodType = paymentMethodType;
  }
}



// WEBPACK FOOTER //
// /codebuild/output/src332225586/src/modules/web/src/js/store/assignments/accept-decline-flow-store.ts